import React, { useEffect, useMemo, useState } from 'react';

import { colors, typography } from '@pray/shared/components/foundations';
import Text from '@pray/shared/components/ui/Text';
import { formatCurrency } from '@pray/shared/utils';
import { cn } from '@pray/shared/utils/styles';

import { amountTypes } from '../../../constants';
import CustomDonationInput from '../../../CustomDonationInput/CustomDonationInput';
import type { DonationForm } from '../../../types';

import styles from './InlineCustomAmount.module.scss';

type InlineCustomAmountProps = {
  form: DonationForm;
  isValidAmount: boolean;
  minimumDonationAmount: number;
};

export default function InlineCustomAmount({ form, isValidAmount, minimumDonationAmount }: InlineCustomAmountProps) {
  const initialValue = `$${minimumDonationAmount / 100}`;
  const [value, setValue] = useState(initialValue);
  const rawValue = useMemo(() => Number(value.replace(/[^0-9]/g, '') || 0), [value]);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
  }

  useEffect(() => {
    const valueInCents = Math.round(rawValue * 100);
    form.setValue('amountType', amountTypes.custom);
    form.setValue('amount', valueInCents);
  }, [rawValue]);

  return (
    <div className={styles.inlineCustomAmountContainer}>
      <Text className={styles.customAmountTitle} variant={typography.heading_lg}>
        Enter donation amount
      </Text>

      <div className={styles.customAmountInputContainer}>
        <CustomDonationInput
          autoFocus
          placeholder="$0"
          defaultValue={initialValue}
          className="w-full p-0 text-center text-3xl font-medium text-[inherit]"
          onChange={handleChange}
        />

        <div className={cn(styles.customAmountInfo, { invisible: isValidAmount })}>
          <Text
            variant={typography.body_md}
            className={styles.customAmountNote}
            color={isValidAmount ? colors.text_secondary : colors.attention}
          >
            Minimum {form.values.interval}ly amount is {formatCurrency(minimumDonationAmount, 0)}.
          </Text>
        </div>
      </div>
    </div>
  );
}
